import { useState } from "react";
import { LegalLayout } from "@/pages/LegalPage";
import apiClient, { formatApiErrorDetail } from "@/lib/apiClient";
import { Loader2, CheckCircle2, Mail, Send } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) return;
    setSending(true);
    try {
      await apiClient.post("/contact", { name, email, message });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <LegalLayout title="Contact us" updated="June 2026">
      <p className="text-[15px]">
        Have a question about Tugboat — pricing, a technical issue, or just want to say ahoy? Drop us a line below or
        email us directly at <a href="mailto:hello@tugboatapp.com" className="font-800 text-orange underline">hello@tugboatapp.com</a>. We read every message.
      </p>

      {sent ? (
        <div className="nb-card bg-grass/15 border-2 border-ink p-8 text-center" data-testid="contact-success">
          <div className="h-14 w-14 mx-auto mb-4 rounded-2xl bg-grass border-2 border-ink flex items-center justify-center shadow-nb-sm">
            <CheckCircle2 className="h-7 w-7 text-white" />
          </div>
          <h2 className="font-display text-2xl font-700 mb-1">Thanks for reaching out! 🚢</h2>
          <p className="text-ink/60 font-600">We'll reply to your email soon.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="nb-card bg-white border-2 border-ink p-6 shadow-nb-sm space-y-4" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="label-overline mb-1.5 block">Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="contact-name"
                placeholder="Your name"
                className="w-full border-2 border-ink rounded-xl px-3.5 py-2.5 font-600 bg-white outline-none focus:shadow-nb-sm" />
            </div>
            <div>
              <label className="label-overline mb-1.5 block">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="contact-email"
                placeholder="you@company.com"
                className="w-full border-2 border-ink rounded-xl px-3.5 py-2.5 font-600 bg-white outline-none focus:shadow-nb-sm" />
            </div>
          </div>
          <div>
            <label className="label-overline mb-1.5 block">Message</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={6} data-testid="contact-message"
              placeholder="How can we help?"
              className="w-full border-2 border-ink rounded-xl px-3.5 py-2.5 font-600 bg-white outline-none focus:shadow-nb-sm resize-y" />
          </div>
          <button type="submit" disabled={sending || !name.trim() || !email.trim() || !message.trim()} data-testid="contact-submit"
            className="w-full flex items-center justify-center gap-2 font-800 px-5 py-3 rounded-xl bg-orange text-white border-2 border-ink shadow-nb-sm hover:translate-y-[-1px] transition-transform disabled:opacity-50 disabled:translate-y-0">
            {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />} Send message
          </button>
        </form>
      )}

      <div className="flex items-center gap-2 text-[15px] text-ink/70">
        <Mail className="h-4 w-4 text-orange" /> Prefer email? <a href="mailto:hello@tugboatapp.com" className="font-800 text-orange underline">hello@tugboatapp.com</a>
      </div>
    </LegalLayout>
  );
}
